import React, { useState, useEffect, useCallback } from "react";
import { TrendingUp, PieChart, CreditCard, Target } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { useSettings, formatAmount } from "../settings/SettingsContext";
import { isInPeriod } from "../utils/period";

import { API_BASE, apiFetch } from "../api";

const DashboardStats = () => {
  const { tokens, fonts } = useTheme();
  const { settings } = useSettings();
  const masked = settings.maskAmounts;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      apiFetch(`${API_BASE}/assets`).then((res) => (res.ok ? res.json() : Promise.reject())),
      apiFetch(`${API_BASE}/loans`).then((res) => (res.ok ? res.json() : Promise.reject())),
      apiFetch(`${API_BASE}/credit-cards`).then((res) => (res.ok ? res.json() : Promise.reject())),
      apiFetch(`${API_BASE}/bills`).then((res) => (res.ok ? res.json() : Promise.reject())),
      apiFetch(`${API_BASE}/budget-plans`).then((res) => (res.ok ? res.json() : Promise.reject())),
    ])
      .then(([assets, loans, cards, bills, plans]) => setData({ assets, loans, cards, bills, plans }))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toneStyles = {
    positive: { bg: tokens.positiveBg, fg: tokens.positive },
    neutral: { bg: tokens.neutralSoft, fg: tokens.textMuted },
    accent: { bg: tokens.accentSoft, fg: tokens.accent },
    pending: { bg: tokens.pendingBg, fg: tokens.pending },
  };

  let stats = [];
  if (data) {
    const totalAssets = data.assets.reduce((sum, a) => sum + a.value, 0);
    const totalOwed = data.loans.reduce((sum, l) => sum + l.remainingBalance, 0);
    const netWorth = totalAssets - totalOwed;

    const monthlyExpenses = data.bills
      .filter((b) => isInPeriod(b.dueDate, "Month"))
      .reduce((sum, b) => sum + b.amount, 0);

    const totalCreditLimit = data.cards.reduce((sum, c) => sum + c.cardLimit, 0);
    const availableCredit = data.cards.reduce((sum, c) => sum + (c.cardLimit - c.balance), 0);

    const totalCollected = data.plans.reduce((sum, p) => sum + p.collected, 0);
    const totalGoal = data.plans.reduce((sum, p) => sum + p.budget, 0);

    stats = [
      {
        label: "Net Worth",
        value: `${netWorth < 0 && !masked ? "-" : ""}${formatAmount(Math.abs(netWorth), masked)}`,
        subtext: `vs ${formatAmount(totalOwed, masked)} owed`,
        icon: TrendingUp,
        tone: "positive",
      },
      {
        label: "Monthly Expenses",
        value: formatAmount(monthlyExpenses, masked),
        subtext: "This month",
        icon: PieChart,
        tone: "neutral",
      },
      {
        label: "Available Credit",
        value: formatAmount(availableCredit, masked),
        subtext: totalCreditLimit > 0 ? `of ${formatAmount(totalCreditLimit, masked)} total limit` : "No cards tracked",
        icon: CreditCard,
        tone: "accent",
      },
      {
        label: "Budget Collected",
        value: formatAmount(totalCollected, masked),
        subtext: totalGoal > 0 ? `of ${formatAmount(totalGoal, masked)} goal` : "No plans yet",
        icon: Target,
        tone: "pending",
      },
    ];
  }

  return (
    <div style={styles.grid}>
      {loading && <p style={{ color: tokens.textMuted, fontSize: 13 }}>Loading...</p>}
      {error && <p style={{ color: tokens.negative, fontSize: 13 }}>Couldn't reach the backend.</p>}
      {!loading &&
        !error &&
        stats.map(({ label, value, subtext, icon: Icon, tone }) => {
          const { bg, fg } = toneStyles[tone];
          return (
            <div
              key={label}
              style={{
                ...styles.tile,
                backgroundColor: tokens.surface,
                border: `1px solid ${tokens.surfaceBorder}`,
                boxShadow: tokens.cardShadow,
              }}
            >
              <div style={styles.tileHeader}>
                <span style={{ ...styles.label, color: tokens.textMuted }}>{label}</span>
                <span style={{ ...styles.iconBadge, backgroundColor: bg }}>
                  <Icon size={15} color={fg} />
                </span>
              </div>
              <span style={{ ...styles.value, fontFamily: fonts.mono, color: tokens.textPrimary }}>{value}</span>
              <span style={{ ...styles.subtext, color: tokens.textMuted }}>{subtext}</span>
            </div>
          );
        })}
    </div>
  );
};

const styles = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 20,
    marginBottom: 24,
  },
  tile: {
    borderRadius: 16,
    padding: "18px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  tileHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  label: {
    fontSize: 12.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  iconBadge: {
    width: 28,
    height: 28,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  value: {
    fontSize: 24,
    fontWeight: 700,
  },
  subtext: {
    fontSize: 12,
    fontWeight: 600,
  },
};

export default DashboardStats;
